import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getShortcodeData = createAsyncThunk(
    'shortcode/getShortcodeData',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.SHORTCODE, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Shortcode data retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch shortcode data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get shortcode data error:', error);
            let errorMessage = 'Failed to fetch shortcode data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch shortcode data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateShortcodeSettings = createAsyncThunk(
    'shortcode/updateSettings',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.SHORTCODE_SETTINGS, data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Shortcode settings updated successfully',
                    shortcode: responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update shortcode settings',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update shortcode settings error:', error);
            let errorMessage = 'Failed to update shortcode settings';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to update shortcode settings';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const saveShortcodeFlow = createAsyncThunk(
    'shortcode/saveFlow',
    async ({ flows, organization_id }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.SHORTCODE_FLOW, { flows, organization_id });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Registration flow saved successfully',
                    flows: data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to save registration flow',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Save shortcode flow error:', error);
            let errorMessage = 'Failed to save registration flow';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to save registration flow';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const saveShortcodeKeywords = createAsyncThunk(
    'shortcode/saveKeywords',
    async ({ keywords, organization_id }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.SHORTCODE_KEYWORDS, { keywords, organization_id });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Keywords saved successfully',
                    keywords: data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to save keywords',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Save shortcode keywords error:', error);
            let errorMessage = 'Failed to save keywords';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to save keywords';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getShortcodeStats = createAsyncThunk(
    'shortcode/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.SHORTCODE_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Shortcode stats retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch shortcode stats',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get shortcode stats error:', error);
            let errorMessage = 'Failed to fetch shortcode stats';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch shortcode stats';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportShortcodeData = createAsyncThunk(
    'shortcode/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.SHORTCODE_EXPORT, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Shortcode data exported successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export shortcode data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Export shortcode data error:', error);
            let errorMessage = 'Failed to export shortcode data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to export shortcode data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    shortcode: null,
    flows: [],
    keywords: [],
    stats: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const shortcodeSlice = createSlice({
    name: 'shortcode',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearShortcodeState: (state) => {
            state.shortcode = null;
            state.flows = [];
            state.keywords = [];
            state.stats = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.isSubmitting = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET SHORTCODE DATA =====
            .addCase(getShortcodeData.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getShortcodeData.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const data = payload.data || {};
                state.shortcode = data.shortcode || null;
                state.flows = data.flows || [];
                state.keywords = data.keywords || [];
                state.stats = data.stats || null;
                state.successMessage = payload.message || 'Shortcode data retrieved successfully';
                state.apiMessage = payload.message || 'Shortcode data retrieved successfully';
                state.error = null;
            })
            .addCase(getShortcodeData.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch shortcode data';
                state.apiMessage = action.payload?.message || 'Failed to fetch shortcode data';
                state.successMessage = null;
            })

            // ===== UPDATE SHORTCODE SETTINGS =====
            .addCase(updateShortcodeSettings.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateShortcodeSettings.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.shortcode = payload.shortcode || state.shortcode;
                state.successMessage = payload.message || 'Shortcode settings updated successfully';
                state.apiMessage = payload.message || 'Shortcode settings updated successfully';
                state.error = null;
            })
            .addCase(updateShortcodeSettings.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update shortcode settings';
                state.apiMessage = action.payload?.message || 'Failed to update shortcode settings';
                state.successMessage = null;
            })

            // ===== SAVE SHORTCODE FLOW =====
            .addCase(saveShortcodeFlow.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(saveShortcodeFlow.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.flows = payload.flows || [];
                state.successMessage = payload.message || 'Registration flow saved successfully';
                state.apiMessage = payload.message || 'Registration flow saved successfully';
                state.error = null;
            })
            .addCase(saveShortcodeFlow.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to save registration flow';
                state.apiMessage = action.payload?.message || 'Failed to save registration flow';
                state.successMessage = null;
            })

            // ===== SAVE SHORTCODE KEYWORDS =====
            .addCase(saveShortcodeKeywords.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(saveShortcodeKeywords.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.keywords = payload.keywords || [];
                state.successMessage = payload.message || 'Keywords saved successfully';
                state.apiMessage = payload.message || 'Keywords saved successfully';
                state.error = null;
            })
            .addCase(saveShortcodeKeywords.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to save keywords';
                state.apiMessage = action.payload?.message || 'Failed to save keywords';
                state.successMessage = null;
            })

            // ===== GET SHORTCODE STATS =====
            .addCase(getShortcodeStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getShortcodeStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = payload.stats || null;
                state.successMessage = payload.message || 'Shortcode stats retrieved successfully';
                state.apiMessage = payload.message || 'Shortcode stats retrieved successfully';
                state.error = null;
            })
            .addCase(getShortcodeStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch shortcode stats';
                state.apiMessage = action.payload?.message || 'Failed to fetch shortcode stats';
                state.successMessage = null;
                state.stats = null;
            })

            // ===== EXPORT SHORTCODE DATA =====
            .addCase(exportShortcodeData.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportShortcodeData.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Shortcode data exported successfully';
                state.apiMessage = payload.message || 'Shortcode data exported successfully';
                state.error = null;
            })
            .addCase(exportShortcodeData.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export shortcode data';
                state.apiMessage = action.payload?.message || 'Failed to export shortcode data';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearShortcodeState,
} = shortcodeSlice.actions;

export const selectShortcode = (state) => state.shortcode?.shortcode || null;
export const selectShortcodeFlows = (state) => state.shortcode?.flows || [];
export const selectShortcodeKeywords = (state) => state.shortcode?.keywords || [];
export const selectShortcodeStats = (state) => state.shortcode?.stats || null;
export const selectShortcodeLoading = (state) => state.shortcode?.isLoading || false;
export const selectShortcodeSubmitting = (state) => state.shortcode?.isSubmitting || false;
export const selectShortcodeApiMessage = (state) => state.shortcode?.apiMessage || null;
export const selectShortcodeError = (state) => state.shortcode?.error || null;

export default shortcodeSlice.reducer;